import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faLifeRing,
    faPlus,
    faTrash,
    faCopy,
    faGripVertical,
    faRotateLeft,
    faFloppyDisk,
    faCircleNotch,
    faEnvelope,
    faEye,
    faChevronDown,
    faChevronRight,
    faInfoCircle,
    faTimes,
    faArrowLeft,
    faArrowRight,
    faUndo,
    faExclamationTriangle,
    faBook,
    faQuestionCircle
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import {
    DragDropContext,
    Droppable,
    Draggable,
    DropResult,
    DroppableProvided,
    DraggableProvided,
    DraggableStateSnapshot
} from 'react-beautiful-dnd';

import SupportSessionMessage from '../../components/support/SupportSessionMessage';
import { SupportFlowDefinition, SupportFlowConfig, SupportFlowVersion } from '../../types/supportFlow';
import { SupportStepDefinition, SupportSessionState } from '../../types/chat';
import { getAllClients, getSupportFlowConfig, saveSupportFlowConfig } from '../../services/api';
import { generateUUID } from '../../utils/uuid';

type StepType = SupportStepDefinition['type'];
type TabType = 'basic' | 'steps' | 'exits' | 'history';

interface ClientSummary {
    id: string;
    name?: string;
    email?: string;
}

interface EditorSnapshot {
    flow: SupportFlowDefinition;
    isEnabled: boolean;
    escalationEmail: string;
}

const STEP_TEMPLATES: Array<{ type: StepType; label: string; helper: string; icon: string }> = [
    { type: 'text', label: 'Short Text', helper: 'Quick one-line note', icon: '📝' },
    { type: 'long_text', label: 'Long Text', helper: 'Collect a longer description', icon: '📄' },
    { type: 'single_select', label: 'Single Choice', helper: 'Pick exactly one answer', icon: '◉' },
    { type: 'multi_select', label: 'Multiple Choice', helper: 'Let them tick several options', icon: '☑' },
    { type: 'contact', label: 'Contact Form', helper: 'Capture name, email, phone', icon: '📧' },
    { type: 'image', label: 'Image Upload', helper: 'Upload screenshots or photos', icon: '🖼️' }
];

const createStep = (type: StepType, explicitId?: string): SupportStepDefinition => {
    const baseId = explicitId || `${type}_${Math.random().toString(36).slice(2, 6)}`;
    const base: SupportStepDefinition = {
        id: baseId,
        type,
        title: 'Untitled step',
        description: '',
        required: true
    };

    switch (type) {
        case 'single_select':
            return {
                ...base,
                options: [
                    { value: `${baseId}_a`, label: 'Example option A' },
                    { value: `${baseId}_b`, label: 'Example option B' }
                ]
            };
        case 'multi_select':
            return {
                ...base,
                allow_other: true,
                options: [
                    { value: `${baseId}_a`, label: 'Example option A' },
                    { value: `${baseId}_b`, label: 'Example option B' },
                    { value: `${baseId}_c`, label: 'Example option C' }
                ]
            };
        case 'text':
            return {
                ...base,
                placeholder: 'Short response'
            };
        case 'long_text':
            return {
                ...base,
                placeholder: 'Describe what you see…',
                min_length: 20
            };
        case 'contact':
            return {
                ...base,
                fields: [
                    { id: 'name', label: 'Full name', type: 'text', required: true },
                    { id: 'email', label: 'Email address', type: 'email', required: true },
                    { id: 'phone', label: 'Phone (optional)', type: 'phone', required: false }
                ]
            };
        case 'image':
            return {
                ...base,
                title: 'Upload Image',
                description: 'Please upload a screenshot or photo to help us understand your issue',
                placeholder: 'Click to select image or drag and drop',
                accept: 'image/*',
                max_size_mb: 10,
                multiple: false
            };
        default:
            return base;
    }
};

const exampleAnswerForStep = (step: SupportStepDefinition): string => {
    switch (step.type) {
        case 'single_select':
            return step.options?.[0]?.label || 'Example choice';
        case 'multi_select': {
            const selections = step.options?.slice(0, 2).map(opt => opt.label) || ['Choice A', 'Choice B'];
            return selections.join(', ');
        }
        case 'contact':
            return 'Name: Jane Doe | Email: jane@example.com | Phone: (555) 123-4567';
        case 'long_text':
            return 'Detailed description about what went wrong, including context and error messages.';
        case 'image':
            return 'Screenshot uploaded (example-image.png)';
        case 'text':
        default:
            return 'Short description or ID';
    }
};

// Tooltip component
const Tooltip: React.FC<{ text: string; children: React.ReactNode }> = ({ text, children }) => {
    const [show, setShow] = useState(false);
    return (
        <div className="relative inline-block">
            <div
                onMouseEnter={() => setShow(true)}
                onMouseLeave={() => setShow(false)}
            >
                {children}
            </div>
            {show && (
                <div className="absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 text-xs text-white bg-gray-900 rounded-lg shadow-lg whitespace-nowrap">
                    {text}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                        <div className="border-4 border-transparent border-t-gray-900"></div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Preview Modal Component
const PreviewModal: React.FC<{
    flow: SupportFlowDefinition;
    isOpen: boolean;
    onClose: () => void;
}> = ({ flow, isOpen, onClose }) => {
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [responses, setResponses] = useState<Array<{ stepId: string; question: string; answer: string }>>([]);

    useEffect(() => {
        if (isOpen) {
            setCurrentStepIndex(0);
            setResponses([]);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const currentStep = flow.steps[currentStepIndex];
    const isLastStep = currentStepIndex === flow.steps.length - 1;

    const handleNext = () => {
        if (currentStep) {
            setResponses(prev => [...prev, {
                stepId: currentStep.id,
                question: currentStep.title || 'Question',
                answer: exampleAnswerForStep(currentStep)
            }]);
        }
        if (!isLastStep) {
            setCurrentStepIndex(prev => prev + 1);
        }
    };

    const handlePrevious = () => {
        if (currentStepIndex > 0) {
            setCurrentStepIndex(prev => prev - 1);
            setResponses(prev => prev.slice(0, -1));
        }
    };

    const handleReset = () => {
        setCurrentStepIndex(0);
        setResponses([]);
    };

    const previewSession: SupportSessionState = {
        sessionId: 'preview-session',
        title: flow.title || 'Troubleshooting Assistant',
        description: flow.description || '',
        introText: "Here's how the assistant will guide your customers.",
        responses,
        currentStep: currentStep ? {
            session_id: 'preview-session',
            flow_title: flow.title,
            flow_description: flow.description,
            step: currentStep,
            responses: {},
            status: 'in_progress',
            step_index: currentStepIndex + 1,
            total_steps: flow.steps.length
        } : undefined,
        summary: undefined,
        status: 'in_progress',
        awaitingNext: false,
        finalMessage: undefined
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                {/* Modal Header */}
                <div className="flex items-center justify-between flex-wrap gap-3 p-6 border-b border-gray-200">
                    <div>
                        <h2 className="text-xl font-semibold text-gray-900">Live Preview</h2>
                        <p className="text-sm text-gray-500 mt-1">See how customers will experience this flow</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition"
                    >
                        <FontAwesomeIcon icon={faTimes} size="lg" />
                    </button>
                </div>

                {/* Preview Controls */}
                <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-b border-gray-200">
                    <button
                        onClick={handlePrevious}
                        disabled={currentStepIndex === 0}
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                        Previous
                    </button>

                    <div className="text-center">
                        <p className="text-sm font-semibold text-gray-900">
                            Step {currentStepIndex + 1} of {flow.steps.length}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">{currentStep?.title || 'Untitled'}</p>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleReset}
                            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap"
                        >
                            <FontAwesomeIcon icon={faUndo} className="mr-2" />
                            Reset
                        </button>
                        <button
                            onClick={handleNext}
                            disabled={isLastStep}
                            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-black rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                        >
                            Next
                            <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                        </button>
                    </div>
                </div>

                {/* Preview Content */}
                <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
                    <div className="max-w-2xl mx-auto">
                        {currentStep ? (
                            <SupportSessionMessage
                                session={previewSession}
                                submitting={false}
                                onSubmitStep={() => Promise.resolve()}
                            />
                        ) : (
                            <div className="text-center py-12 text-gray-500">
                                <p>No steps configured yet</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Modal Footer */}
                <div className="flex items-center justify-end px-6 py-4 border-t border-gray-200 bg-white">
                    <button
                        onClick={onClose}
                        className="px-6 py-2 text-sm font-medium text-white bg-gray-600 rounded-lg hover:bg-gray-700"
                    >
                        Close Preview
                    </button>
                </div>
            </div>
        </div>
    );
};

// Unsaved Changes Warning Modal
const UnsavedChangesModal: React.FC<{
    isOpen: boolean;
    onDiscard: () => void;
    onCancel: () => void;
}> = ({ isOpen, onDiscard, onCancel }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
                <div className="flex items-center gap-3 mb-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="text-amber-600" size="lg" />
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">Unsaved Changes</h3>
                        <p className="text-sm text-gray-500">You have unsaved changes that will be lost.</p>
                    </div>
                </div>
                <p className="text-sm text-gray-600 mb-6">
                    Are you sure you want to switch clients without saving? All your changes will be discarded.
                </p>
                <div className="flex items-center justify-end gap-3">
                    <button
                        onClick={onCancel}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onDiscard}
                        className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                    >
                        Discard Changes
                    </button>
                </div>
            </div>
        </div>
    );
};

const SupportFlowEditor: React.FC = () => {
    const [activeTab, setActiveTab] = useState<TabType>('basic');
    const [clients, setClients] = useState<ClientSummary[]>([]);
    // Clients are fetched unconditionally on mount, so this starts true: with it
    // false the very first render had no flow and nothing marked as loading, which
    // fell through to the "No support flow found" state and flashed it on screen.
    const [clientsLoading, setClientsLoading] = useState(true);
    // Whether a config load has finished (successfully or not). Until it has, an
    // absent flow means "not fetched yet", not "there isn't one".
    const [configAttempted, setConfigAttempted] = useState(false);
    const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
    const [pendingClientId, setPendingClientId] = useState<string | null>(null);

    const [flow, setFlow] = useState<SupportFlowDefinition | null>(null);
    const [isEnabled, setIsEnabled] = useState(true);
    const [escalationEmail, setEscalationEmail] = useState('');
    const [versions, setVersions] = useState<SupportFlowVersion[]>([]);
    const [loadingConfig, setLoadingConfig] = useState(false);
    const [savingConfig, setSavingConfig] = useState(false);
    const [expandedStepId, setExpandedStepId] = useState<string | null>(null);
    const [showPreview, setShowPreview] = useState(false);
    const [showUnsavedModal, setShowUnsavedModal] = useState(false);
    const [autoSaveEnabled] = useState(true);
    const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const initialSnapshotRef = useRef<EditorSnapshot | null>(null);
    // Bumped every time the saved baseline is replaced. isDirty compares the editor
    // against that baseline, but a ref assignment cannot re-run a useMemo — so without
    // this the "Unsaved changes" banner stayed up after a successful save whenever the
    // server echoed back exactly what was sent (nothing in state changed, so the memo
    // never recomputed and kept its old `true`).
    const [savedBaseline, setSavedBaseline] = useState(0);

    // Load clients
    useEffect(() => {
        const loadClients = async () => {
            setClientsLoading(true);
            try {
                const response = await getAllClients();
                const list = Array.isArray(response) ? response : response?.clients || [];
                setClients(list);
                if (list.length > 0 && !selectedClientId) {
                    setSelectedClientId(list[0].id);
                }
            } catch (error) {
                console.error('Failed to load clients', error);
                toast.error('Unable to load clients for support flow editor.');
            } finally {
                setClientsLoading(false);
            }
        };

        loadClients();
    }, []);

    const normalizePostActions = (definition: SupportFlowDefinition) => {
        const next = { ...(definition.post_actions || {}) };
        if (next.email) {
            const to = next.email.to;
            if (typeof to === 'string') {
                next.email.to = to
                    .split(',')
                    .map((item: string) => item.trim())
                    .filter(Boolean);
            }
            if (!Array.isArray(next.email.to)) {
                next.email.to = [];
            }
        }
        definition.post_actions = next;
    };

    const loadConfig = useCallback(async (targetId: string) => {
        setLoadingConfig(true);
        try {
            const response = await getSupportFlowConfig(targetId);
            const config: SupportFlowConfig = response.config;
            const definition: SupportFlowDefinition = {
                title: config.title || 'Troubleshooting Assistant',
                description: config.description || '',
                steps: (config.steps || []).map(step => ({ ...step })),
                exit_rules: ((config as any).exit_rules || []).map((r: any) => ({ ...r })),
                priority: (config as any).priority === 'high' ? 'high' : 'normal',
                trigger_description: (config as any).trigger_description || '',
                post_actions: JSON.parse(JSON.stringify(config.post_actions || {})),
                // No editor field for this yet; carried through so saving the flow
                // doesn't silently discard it.
                returning_visitor: (config as any).returning_visitor
            };
            normalizePostActions(definition);

            setFlow(definition);
            setIsEnabled(config.is_enabled === true);

            const emailArray = definition.post_actions?.email?.to;
            const joinedRecipients = Array.isArray(emailArray) ? emailArray.join(', ') : '';
            setEscalationEmail(config.escalation_email || joinedRecipients);
            setVersions(response.versions || []);
            setExpandedStepId(definition.steps[0]?.id ?? null);

            initialSnapshotRef.current = {
                flow: JSON.parse(JSON.stringify(definition)),
                isEnabled: config.is_enabled === true,
                escalationEmail: (config.escalation_email || joinedRecipients).trim()
            };
            setSavedBaseline(n => n + 1);
        } catch (error) {
            console.error('Failed to load support flow', error);
            toast.error('Unable to load support flow configuration.');
            setFlow(null);
            setVersions([]);
        } finally {
            setLoadingConfig(false);
            setConfigAttempted(true);
        }
    }, []);

    useEffect(() => {
        if (selectedClientId) {
            loadConfig(selectedClientId);
        }
    }, [selectedClientId, loadConfig]);

    const updateFlow = (updater: (prev: SupportFlowDefinition) => SupportFlowDefinition) => {
        setFlow(prev => {
            if (!prev) return prev;
            return updater(prev);
        });
    };

    const handleFlowFieldChange = (field: keyof SupportFlowDefinition, value: string) => {
        updateFlow(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleGuidancePromptChange = (value: string) => {
        updateFlow(prev => ({
            ...prev,
            post_actions: {
                ...(prev.post_actions || {}),
                guidance_prompt: value
            }
        }));
    };

    const handleEmailToggle = (enabled: boolean) => {
        updateFlow(prev => {
            const email = { ...(prev.post_actions?.email || {}) };
            email.enabled = enabled;
            if (!Array.isArray(email.to)) {
                email.to = [];
            }
            return {
                ...prev,
                post_actions: {
                    ...(prev.post_actions || {}),
                    email
                }
            };
        });
    };

    const handleEmailRecipientsChange = (value: string) => {
        const recipients = value
            .split(',')
            .map(item => item.trim())
            .filter(Boolean);
        updateFlow(prev => ({
            ...prev,
            post_actions: {
                ...(prev.post_actions || {}),
                email: {
                    ...(prev.post_actions?.email || {}),
                    to: recipients
                }
            }
        }));
        setEscalationEmail(value);
    };

    const handleWebhookToggle = (enabled: boolean) => {
        updateFlow(prev => ({
            ...prev,
            post_actions: {
                ...(prev.post_actions || {}),
                webhook: {
                    provider: 'smith.ai',
                    url: '',
                    ...(prev.post_actions?.webhook || {}),
                    enabled
                }
            }
        }));
    };

    const handleWebhookUrlChange = (value: string) => {
        updateFlow(prev => ({
            ...prev,
            post_actions: {
                ...(prev.post_actions || {}),
                webhook: {
                    provider: 'smith.ai',
                    ...(prev.post_actions?.webhook || {}),
                    url: value
                }
            }
        }));
    };

    const handleWebhookTokenChange = (value: string) => {
        updateFlow(prev => ({
            ...prev,
            post_actions: {
                ...(prev.post_actions || {}),
                webhook: {
                    provider: 'smith.ai',
                    ...(prev.post_actions?.webhook || {}),
                    auth_token: value
                }
            }
        }));
    };

    const handleAddStep = (type: StepType, insertAfterIndex?: number) => {
        const template = createStep(type);
        updateFlow(prev => {
            const steps = [...prev.steps];
            if (typeof insertAfterIndex === 'number') {
                steps.splice(insertAfterIndex + 1, 0, template);
            } else {
                steps.push(template);
            }
            return { ...prev, steps };
        });
        setExpandedStepId(template.id);
    };

    const handleDuplicateStep = (stepId: string) => {
        if (!flow) return;
        const idx = flow.steps.findIndex(step => step.id === stepId);
        if (idx === -1) return;
        const clone: SupportStepDefinition = {
            ...flow.steps[idx],
            id: `${stepId}_${Math.random().toString(36).slice(2, 5)}`
        };
        updateFlow(prev => {
            const steps = [...prev.steps];
            steps.splice(idx + 1, 0, clone);
            return { ...prev, steps };
        });
        setExpandedStepId(clone.id);
    };

    /**
     * Drop visibility conditions that no longer point at an EARLIER step, which can
     * happen after a reorder or a delete. Without this the editor could build a flow
     * the backend refuses to save, with the error pointing at a step the consultant
     * never touched.
     */
    const pruneVisibility = (steps: SupportStepDefinition[]): SupportStepDefinition[] =>
        steps.map((step, idx) => {
            if (!step.visible_if) return step;
            const allowed = new Set(steps.slice(0, idx).map(s => s.id));
            const conditions = step.visible_if.conditions.filter(c => allowed.has(c.step));
            if (conditions.length === step.visible_if.conditions.length) return step;
            if (conditions.length === 0) {
                const { visible_if, ...rest } = step;
                return rest as SupportStepDefinition;
            }
            return { ...step, visible_if: { ...step.visible_if, conditions } };
        });

    const countConditions = (steps: SupportStepDefinition[]) =>
        steps.reduce((n, s) => n + (s.visible_if?.conditions.length || 0), 0);

    const handleRemoveStep = (stepId: string) => {
        if (!flow) return;
        if (flow.steps.length <= 1) {
            toast.warn('Keep at least one step to maintain a valid flow.');
            return;
        }
        const dependents = flow.steps.filter(
            s => s.id !== stepId && s.visible_if?.conditions.some(c => c.step === stepId)
        );
        if (dependents.length > 0) {
            const names = dependents.map(s => `"${s.title || s.id}"`).join(', ');
            toast.info(`Removed the condition on ${names}, which depended on this question.`);
        }
        updateFlow(prev => {
            const steps = pruneVisibility(prev.steps.filter(step => step.id !== stepId));
            if (expandedStepId === stepId) {
                setExpandedStepId(steps[0]?.id ?? null);
            }
            return { ...prev, steps };
        });
    };

    const handleDragEnd = (result: DropResult) => {
        if (!result.destination || !flow) return;

        const { source, destination } = result;
        if (source.index === destination.index) return;

        const reordered = Array.from(flow.steps);
        const [moved] = reordered.splice(source.index, 1);
        reordered.splice(destination.index, 0, moved);
        const pruned = pruneVisibility(reordered);
        if (countConditions(pruned) < countConditions(reordered)) {
            toast.info('A condition was removed — a question can only depend on one asked before it.');
        }

        updateFlow(prev => {
            const steps = Array.from(prev.steps);
            const [removed] = steps.splice(source.index, 1);
            steps.splice(destination.index, 0, removed);
            return { ...prev, steps: pruneVisibility(steps) };
        });
    };

    const handleStepIdChange = (stepId: string, nextId: string) => {
        const normalized = nextId
            .trim()
            .replace(/\s+/g, '_')
            .replace(/[^a-zA-Z0-9_-]/g, '')
            .toLowerCase();
        if (!normalized) {
            toast.warn('Step ID cannot be empty.');
            return;
        }
        if (flow?.steps.some(step => step.id === normalized && step.id !== stepId)) {
            toast.warn('Step IDs must be unique.');
            return;
        }
        // Renaming a step must carry its references with it. Exit rules and visibility
        // conditions both address steps by id, and a stale reference makes the whole
        // flow unsavable with an error naming a step the consultant didn't edit.
        updateFlow(prev => ({
            ...prev,
            steps: prev.steps.map(step => {
                const renamed = step.id === stepId ? { ...step, id: normalized } : { ...step };
                if (renamed.visible_if) {
                    renamed.visible_if = {
                        ...renamed.visible_if,
                        conditions: renamed.visible_if.conditions.map(c =>
                            c.step === stepId ? { ...c, step: normalized } : c
                        )
                    };
                }
                return renamed;
            }),
            exit_rules: (prev.exit_rules || []).map(rule => ({
                ...rule,
                conditions: (rule.conditions || []).map(c =>
                    c.step === stepId ? { ...c, step: normalized } : c
                )
            }))
        }));
        if (expandedStepId === stepId) {
            setExpandedStepId(normalized);
        }
    };

    const handleStepUpdate = (stepId: string, updates: Partial<SupportStepDefinition>) => {
        updateFlow(prev => ({
            ...prev,
            steps: prev.steps.map(step =>
                step.id === stepId ? { ...step, ...updates } : step
            )
        }));
    };

    // --- Conditional visibility ("only ask this question if…") -------------------
    // A condition may only reference an EARLIER step: by the time this step is
    // reached, a later answer cannot exist, so the question would never appear. The
    // backend rejects that on save, so the pickers below only offer earlier steps.
    const earlierSteps = (stepId: string): SupportStepDefinition[] => {
        if (!flow) return [];
        const idx = flow.steps.findIndex(s => s.id === stepId);
        return idx <= 0 ? [] : flow.steps.slice(0, idx);
    };

    const handleVisibilityToggle = (stepId: string, enabled: boolean) => {
        if (!enabled) {
            updateFlow(prev => ({
                ...prev,
                steps: prev.steps.map(step => {
                    if (step.id !== stepId) return step;
                    const { visible_if, ...rest } = step;
                    return rest as SupportStepDefinition;
                })
            }));
            return;
        }
        const candidates = earlierSteps(stepId);
        const first = candidates[candidates.length - 1];
        if (!first) return;
        const firstValue = first.options?.[0]?.value ?? '';
        handleStepUpdate(stepId, {
            visible_if: {
                match: 'all',
                conditions: [{ step: first.id, op: 'equals', value: String(firstValue) }]
            }
        });
    };

    const handleVisibilityMatch = (stepId: string, match: 'all' | 'any') => {
        const step = flow?.steps.find(s => s.id === stepId);
        if (!step?.visible_if) return;
        handleStepUpdate(stepId, { visible_if: { ...step.visible_if, match } });
    };

    const handleVisibilityCondition = (
        stepId: string,
        index: number,
        patch: Partial<{ step: string; op: string; value: string }>
    ) => {
        const step = flow?.steps.find(s => s.id === stepId);
        if (!step?.visible_if) return;
        const conditions = step.visible_if.conditions.map((c, i) => {
            if (i !== index) return c;
            const next = { ...c, ...patch };
            // Switching the referenced step: preselect one of its options so the
            // condition can never be saved with a value that step cannot produce.
            if (patch.step && patch.step !== c.step) {
                const target = flow?.steps.find(s => s.id === patch.step);
                next.value = String(target?.options?.[0]?.value ?? '');
            }
            return next;
        });
        handleStepUpdate(stepId, { visible_if: { ...step.visible_if, conditions } });
    };

    const handleVisibilityConditionAdd = (stepId: string) => {
        const step = flow?.steps.find(s => s.id === stepId);
        if (!step?.visible_if) return;
        const candidates = earlierSteps(stepId);
        const target = candidates[candidates.length - 1];
        if (!target) return;
        handleStepUpdate(stepId, {
            visible_if: {
                ...step.visible_if,
                conditions: [
                    ...step.visible_if.conditions,
                    { step: target.id, op: 'equals', value: String(target.options?.[0]?.value ?? '') }
                ]
            }
        });
    };

    const handleVisibilityConditionRemove = (stepId: string, index: number) => {
        const step = flow?.steps.find(s => s.id === stepId);
        if (!step?.visible_if) return;
        const conditions = step.visible_if.conditions.filter((_, i) => i !== index);
        if (conditions.length === 0) {
            handleVisibilityToggle(stepId, false);
            return;
        }
        handleStepUpdate(stepId, { visible_if: { ...step.visible_if, conditions } });
    };

    const handleStepTypeChange = (stepId: string, type: StepType) => {
        updateFlow(prev => ({
            ...prev,
            steps: prev.steps.map(step => {
                if (step.id !== stepId) return step;
                if (
                    ['single_select', 'multi_select'].includes(step.type) &&
                    ['single_select', 'multi_select'].includes(type)
                ) {
                    return { ...step, type };
                }
                const template = createStep(type, stepId);
                return {
                    ...template,
                    title: step.title,
                    description: step.description,
                    required: step.required
                };
            })
        }));
    };

    const handleOptionUpdate = (
        stepId: string,
        index: number,
        field: 'label' | 'value',
        value: string
    ) => {
        updateFlow(prev => ({
            ...prev,
            steps: prev.steps.map(step => {
                if (step.id !== stepId || !step.options) return step;
                const options = [...step.options];
                options[index] = { ...options[index], [field]: value };
                return { ...step, options };
            })
        }));
    };

    const handleAddOption = (stepId: string) => {
        updateFlow(prev => ({
            ...prev,
            steps: prev.steps.map(step => {
                if (step.id !== stepId) return step;
                const options = [...(step.options || [])];
                options.push({
                    value: `${step.id}_${generateUUID().slice(0, 6)}`,
                    label: 'New option'
                });
                return { ...step, options };
            })
        }));
    };

    const handleRemoveOption = (stepId: string, index: number) => {
        updateFlow(prev => ({
            ...prev,
            steps: prev.steps.map(step => {
                if (step.id !== stepId || !step.options) return step;
                const options = step.options.filter((_, idx) => idx !== index);
                if (!options.length) {
                    toast.warn('Keep at least one option.');
                    return step;
                }
                return { ...step, options };
            })
        }));
    };

    const handleContactFieldUpdate = (
        stepId: string,
        fieldIdx: number,
        key: 'label' | 'id' | 'type' | 'required' | 'placeholder',
        value: string | boolean
    ) => {
        updateFlow(prev => ({
            ...prev,
            steps: prev.steps.map(step => {
                if (step.id !== stepId || !step.fields) return step;
                const fields = [...step.fields];
                fields[fieldIdx] = { ...fields[fieldIdx], [key]: value };
                return { ...step, fields };
            })
        }));
    };

    const handleAddContactField = (stepId: string) => {
        updateFlow(prev => ({
            ...prev,
            steps: prev.steps.map(step => {
                if (step.id !== stepId) return step;
                const fields = [...(step.fields || [])];
                fields.push({
                    id: `field_${generateUUID().slice(0, 6)}`,
                    label: 'New field',
                    type: 'text',
                    required: false
                });
                return { ...step, fields };
            })
        }));
    };

    const handleRemoveContactField = (stepId: string, fieldIdx: number) => {
        updateFlow(prev => ({
            ...prev,
            steps: prev.steps.map(step => {
                if (step.id !== stepId || !step.fields) return step;
                if (step.fields.length <= 1) {
                    toast.warn('Keep at least one contact field.');
                    return step;
                }
                const fields = step.fields.filter((_, idx) => idx !== fieldIdx);
                return { ...step, fields };
            })
        }));
    };

    const snapshotSignature = useMemo(() => {
        if (!flow) return '';
        return JSON.stringify({
            flow,
            isEnabled,
            escalationEmail: escalationEmail.trim()
        });
    }, [flow, isEnabled, escalationEmail]);

    const isDirty = useMemo(() => {
        if (!initialSnapshotRef.current) return false;
        return JSON.stringify(initialSnapshotRef.current) !== snapshotSignature;
        // savedBaseline is a dependency on purpose: the baseline lives in a ref, and a
        // ref assignment does not invalidate a memo, so a save that changed nothing in
        // state would otherwise leave this stuck at its previous value.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [snapshotSignature, savedBaseline]);

    const handleReset = useCallback(() => {
        const snapshot = initialSnapshotRef.current;
        if (!snapshot) return;
        const cloned = JSON.parse(JSON.stringify(snapshot.flow));
        setFlow(cloned);
        setIsEnabled(snapshot.isEnabled);
        setEscalationEmail(snapshot.escalationEmail);
        setExpandedStepId(cloned.steps[0]?.id ?? null);
        toast.success('Changes reset successfully.');
    }, []);

    const handleSave = useCallback(async () => {
        if (!flow) return;
        if (!selectedClientId) {
            toast.error('Please select a client first.');
            return;
        }

        setSavingConfig(true);
        try {
            const definitionToSave: SupportFlowDefinition = {
                title: flow.title,
                description: flow.description,
                steps: flow.steps.map(step => ({ ...step })),
                exit_rules: (flow.exit_rules || []).map(r => ({ ...r })),
                priority: flow.priority === 'high' ? 'high' : 'normal',
                trigger_description: flow.trigger_description || '',
                post_actions: {
                    ...(flow.post_actions || {}),
                    guidance_prompt: flow.post_actions?.guidance_prompt,
                    email: {
                        enabled: flow.post_actions?.email?.enabled !== false,
                        to: flow.post_actions?.email?.to || []
                    }
                },
                // Preserved rather than edited here: dropping it would switch off
                // returning-visitor recognition the first time anyone saves the flow.
                returning_visitor: flow.returning_visitor
            };

            const payload = {
                definition: definitionToSave,
                is_enabled: isEnabled,
                escalation_email: escalationEmail.trim() || undefined
            };

            const response = await saveSupportFlowConfig(selectedClientId, payload);

            const savedConfig: SupportFlowConfig = response.config;
            const definition: SupportFlowDefinition = {
                title: savedConfig.title || flow.title,
                description: savedConfig.description || flow.description,
                steps: (savedConfig.steps || []).map(step => ({ ...step })),
                exit_rules: ((savedConfig as any).exit_rules || []).map((r: any) => ({ ...r })),
                priority: (savedConfig as any).priority === 'high' ? 'high' : 'normal',
                trigger_description: (savedConfig as any).trigger_description || '',
                post_actions: JSON.parse(JSON.stringify(savedConfig.post_actions || {})),
                returning_visitor: (savedConfig as any).returning_visitor
            };
            normalizePostActions(definition);

            setFlow(definition);
            setIsEnabled(savedConfig.is_enabled !== false);

            const emailArray = definition.post_actions?.email?.to;
            const joinedRecipients = Array.isArray(emailArray) ? emailArray.join(', ') : '';
            setEscalationEmail(savedConfig.escalation_email || joinedRecipients);
            setVersions(response.versions || []);

            initialSnapshotRef.current = {
                flow: JSON.parse(JSON.stringify(definition)),
                isEnabled: savedConfig.is_enabled !== false,
                escalationEmail: (savedConfig.escalation_email || joinedRecipients).trim()
            };
            setSavedBaseline(n => n + 1);

            toast.success('Support flow saved successfully.');
        } catch (error: any) {
            console.error('Failed to save support flow', error);
            const message = error?.response?.data?.detail || error?.message || 'Unable to save support flow.';
            toast.error(message);
        } finally {
            setSavingConfig(false);
        }
    }, [flow, isEnabled, escalationEmail, selectedClientId]);

    // Auto-save functionality
    useEffect(() => {
        if (!autoSaveEnabled || !isDirty) return;

        if (autoSaveTimerRef.current) {
            clearTimeout(autoSaveTimerRef.current);
        }

        autoSaveTimerRef.current = setTimeout(() => {
            console.log('Auto-save triggered');
            // In production, you might save to draft instead of actual save
            // handleSave();
        }, 30000); // 30 seconds

        return () => {
            if (autoSaveTimerRef.current) {
                clearTimeout(autoSaveTimerRef.current);
            }
        };
    }, [isDirty, autoSaveEnabled]);

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // Cmd/Ctrl + S to save
            if ((e.metaKey || e.ctrlKey) && e.key === 's') {
                e.preventDefault();
                if (isDirty && !savingConfig) {
                    handleSave();
                }
            }
            // Cmd/Ctrl + Z to reset
            if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
                if (isDirty) {
                    e.preventDefault();
                    handleReset();
                }
            }
            // Escape to close modals
            if (e.key === 'Escape') {
                if (showPreview) {
                    setShowPreview(false);
                }
                if (showUnsavedModal) {
                    setShowUnsavedModal(false);
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isDirty, savingConfig, showPreview, showUnsavedModal, handleSave, handleReset]);

    const handleClientChange = (newClientId: string | null) => {
        if (isDirty) {
            setPendingClientId(newClientId);
            setShowUnsavedModal(true);
        } else {
            setSelectedClientId(newClientId);
        }
    };

    const handleDiscardAndSwitch = () => {
        setSelectedClientId(pendingClientId);
        setShowUnsavedModal(false);
        setPendingClientId(null);
        // Reset to initial snapshot
        if (initialSnapshotRef.current) {
            const cloned = JSON.parse(JSON.stringify(initialSnapshotRef.current.flow));
            setFlow(cloned);
            setIsEnabled(initialSnapshotRef.current.isEnabled);
            setEscalationEmail(initialSnapshotRef.current.escalationEmail);
        }
    };

    const activeClient = clients.find(client => client.id === selectedClientId);

    // Still working it out: clients in flight, config in flight, or a client is
    // selected and its config has not come back yet.
    const stillResolving = clientsLoading || loadingConfig || (!!selectedClientId && !configAttempted);

    if (!flow && stillResolving) {
        return (
            <div className="flex min-h-[60vh] items-center justify-center text-sm text-gray-500">
                <FontAwesomeIcon icon={faCircleNotch} spin className="mr-2" />
                Loading support flow…
            </div>
        );
    }

    if (!flow) {
        return (
            <div className="mx-auto max-w-xl space-y-4 rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-brand-100 text-brand-500">
                    <FontAwesomeIcon icon={faLifeRing} />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">No support flow found</h2>
                <p className="text-sm text-gray-500">
                    We couldn't load the configuration. Try refreshing the page or switching the scope.
                </p>
                <button
                    type="button"
                    className="inline-flex items-center justify-center rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-gray-800 whitespace-nowrap"
                    onClick={() => selectedClientId && loadConfig(selectedClientId)}
                    disabled={!selectedClientId}
                >
                    Reload support flow
                </button>
            </div>
        );
    }

    const canSave = isDirty && !savingConfig && !!selectedClientId;

    return (
        <>
            <div className="mx-auto max-w-7xl space-y-6 p-6 lg:p-10">
                {/* Header */}
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex items-start gap-3">
                        <span className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-black text-white">
                            <FontAwesomeIcon icon={faLifeRing} />
                        </span>
                        <div>
                            <h1 className="text-xl font-semibold text-gray-900">Support Flow Builder</h1>
                            <p className="mt-1 text-sm text-gray-600">
                                Configure troubleshooting flows for your clients
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Tooltip text="View documentation">
                            <button
                                type="button"
                                className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white p-2 text-gray-600 hover:bg-gray-50"
                            >
                                <FontAwesomeIcon icon={faBook} />
                            </button>
                        </Tooltip>
                        <Tooltip text="Get help">
                            <button
                                type="button"
                                className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white p-2 text-gray-600 hover:bg-gray-50"
                            >
                                <FontAwesomeIcon icon={faQuestionCircle} />
                            </button>
                        </Tooltip>
                    </div>
                </div>

                {/* Unsaved changes banner */}
                {isDirty && (
                    <div className="flex items-center justify-between rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
                        <div className="flex items-center gap-2">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="text-amber-600" />
                            <span className="text-sm font-medium text-amber-900">
                                Unsaved changes for {activeClient?.name || activeClient?.email || 'selected client'}
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleReset}
                                className="text-sm font-medium text-amber-700 hover:text-amber-800"
                            >
                                Discard
                            </button>
                            <button
                                onClick={handleSave}
                                className="inline-flex items-center rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700 whitespace-nowrap"
                            >
                                Save Now
                            </button>
                        </div>
                    </div>
                )}

                {/* Client Selector */}
                <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                        <span className="text-lg">👤</span>
                        <h2 className="text-lg font-semibold text-gray-900">Select Client to Configure</h2>
                        <Tooltip text="Choose which client's support flow you want to edit">
                            <FontAwesomeIcon icon={faInfoCircle} className="text-gray-400 text-sm ml-1" />
                        </Tooltip>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Client
                        </label>
                        <select
                            value={selectedClientId ?? ''}
                            onChange={(e) => handleClientChange(e.target.value || null)}
                            className="block w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                            disabled={clientsLoading}
                        >
                            <option value="">Select a client...</option>
                            {clientsLoading && <option value="">Loading clients…</option>}
                            {!clientsLoading && clients.length === 0 && <option value="">No clients available</option>}
                            {clients.map(client => (
                                <option key={client.id} value={client.id}>
                                    {client.name || client.email || client.id}
                                </option>
                            ))}
                        </select>
                        <p className="mt-2 text-xs text-gray-500">
                            Support flow settings will apply only to this selected client
                        </p>
                    </div>

                    {activeClient && (
                        <div className="mt-4 flex items-center gap-2 rounded-lg bg-brand-50 border border-brand-200 px-4 py-2">
                            <span className="text-brand-600 font-medium text-sm">✓ Currently editing:</span>
                            <span className="text-sm text-brand-900 font-semibold">
                                {activeClient.name || activeClient.email}
                            </span>
                        </div>
                    )}
                </div>

                {/* Tabs */}
                <div className="border-b border-gray-200">
                    <nav className="flex gap-8">
                        <button
                            onClick={() => setActiveTab('basic')}
                            className={`pb-4 px-1 text-sm font-medium border-b-2 transition ${
                                activeTab === 'basic'
                                    ? 'border-brand-500 text-brand-500'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            Basic Settings
                        </button>
                        <button
                            onClick={() => setActiveTab('steps')}
                            className={`pb-4 px-1 text-sm font-medium border-b-2 transition ${
                                activeTab === 'steps'
                                    ? 'border-brand-500 text-brand-500'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            Steps ({flow.steps.length})
                        </button>
                        <button
                            onClick={() => setActiveTab('exits')}
                            className={`pb-4 px-1 text-sm font-medium border-b-2 transition ${
                                activeTab === 'exits'
                                    ? 'border-brand-500 text-brand-500'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            Exit Rules ({(flow.exit_rules || []).length})
                        </button>
                        <button
                            onClick={() => {
                                setActiveTab('history');
                                if (versions.length === 0 && selectedClientId) {
                                    // Reload versions when clicking history tab
                                    loadConfig(selectedClientId);
                                }
                            }}
                            className={`pb-4 px-1 text-sm font-medium border-b-2 transition ${
                                activeTab === 'history'
                                    ? 'border-brand-500 text-brand-500'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            History
                        </button>
                    </nav>
                </div>

                {/* Tab Content */}
                <div className="min-h-[400px]">
                    {/* Basic Settings Tab */}
                    {activeTab === 'basic' && (
                        <div className="space-y-6">
                            {/* Flow Status */}
                            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                                <div className="flex items-center gap-2 mb-4">
                                    <span className="text-lg">⚡</span>
                                    <h3 className="text-base font-semibold text-gray-900">Flow Status</h3>
                                </div>
                                <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                                    <div>
                                        <p className="font-medium text-gray-900">
                                            Enable Support Flow for this client
                                        </p>
                                        <p className="text-sm text-gray-500 mt-1">
                                            {isEnabled ? '✓ Currently live for this client' : '⊗ Currently disabled for this client'}
                                        </p>
                                    </div>
                                    <label className="relative inline-flex cursor-pointer items-center">
                                        <input
                                            type="checkbox"
                                            className="peer sr-only"
                                            checked={isEnabled}
                                            onChange={(e) => setIsEnabled(e.target.checked)}
                                        />
                                        <span className="peer h-6 w-11 rounded-full bg-gray-300 after:absolute after:start-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all peer-checked:bg-brand-500 peer-checked:after:translate-x-full"></span>
                                    </label>
                                </div>
                                <p className="mt-3 text-xs text-gray-500 flex items-center gap-1">
                                    <FontAwesomeIcon icon={faInfoCircle} />
                                    When disabled, this client's users will see the standard open-ended chat interface
                                </p>

                                {/* Priority */}
                                <div className={`mt-4 flex items-center justify-between rounded-lg border px-4 py-3 ${isEnabled ? 'border-gray-200 bg-gray-50' : 'border-gray-100 bg-gray-50 opacity-60'}`}>
                                    <div>
                                        <p className="font-medium text-gray-900">Priority</p>
                                        <p className="text-sm text-gray-500 mt-1">
                                            {flow?.priority === 'high'
                                                ? 'High — for questions covered by both the knowledge base and this flow, the support assistant answers'
                                                : 'Normal — the support flow runs only when the user clearly asks a support-type question'}
                                        </p>
                                    </div>
                                    <select
                                        disabled={!isEnabled}
                                        value={flow?.priority === 'high' ? 'high' : 'normal'}
                                        onChange={(e) => setFlow(prev => (prev ? { ...prev, priority: e.target.value as 'high' | 'normal' } : prev))}
                                        className="border border-gray-300 rounded-md px-3 py-2 bg-white text-sm disabled:opacity-50"
                                    >
                                        <option value="normal">Normal</option>
                                        <option value="high">High</option>
                                    </select>
                                </div>
                                <p className="mt-2 text-xs text-gray-500 flex items-center gap-1">
                                    <FontAwesomeIcon icon={faInfoCircle} />
                                    With High priority, the support assistant takes over a query when it overlaps this flow's topic; unrelated questions still use the knowledge base. After a visitor completes (or exits) the flow, follow-ups use the standard chat.
                                </p>

                                {/* Trigger description (used by High priority to decide when to take over) */}
                                {flow?.priority === 'high' && (() => {
                                    const TRIGGER_MAX = 600;
                                    const len = (flow?.trigger_description || '').length;
                                    return (
                                        <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                                            <label className="block font-medium text-gray-900">When should this assistant take over?</label>
                                            <p className="text-sm text-gray-500 mt-1 mb-2">
                                                Describe, in plain language, the questions/topics that should start this flow — keep it to 1–3 focused sentences (long descriptions match less accurately). We match incoming questions against this. Leave blank to infer it from the flow's title, description, and questions.
                                            </p>
                                            <textarea
                                                value={flow?.trigger_description || ''}
                                                onChange={(e) => setFlow(prev => (prev ? { ...prev, trigger_description: e.target.value.slice(0, TRIGGER_MAX) } : prev))}
                                                rows={3}
                                                maxLength={TRIGGER_MAX}
                                                placeholder="e.g. Questions about applying for or qualifying for SSDI/SSI disability benefits — eligibility, work history, having a diagnosis, prior representation, or overpayments."
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                                            />
                                            <div className={`mt-1 text-xs text-right ${len > TRIGGER_MAX * 0.9 ? 'text-amber-600' : 'text-gray-400'}`}>
                                                {len}/{TRIGGER_MAX}
                                            </div>
                                        </div>
                                    );
                                })()}

                                {/* Reply for someone who already finished the screening and comes back.
                                    Writing a message is also what switches the behaviour on: left blank,
                                    a returning visitor is screened again exactly as before. */}
                                {(() => {
                                    const RETURNING_MAX = 600;
                                    const message = flow?.returning_visitor?.message || '';
                                    return (
                                        <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                                            <label className="block font-medium text-gray-900">What to say to someone who has already been screened</label>
                                            <p className="text-sm text-gray-500 mt-1 mb-2">
                                                Someone who completed the questions and comes back later to chase their enquiry
                                                shouldn't be asked everything again. Write what they should be told instead — usually
                                                how to reach the office directly. <strong>Leave this blank</strong> and nothing changes:
                                                they'll go through the questions again, as they do today. Only people who actually
                                                finished and gave their details see this; anyone who was declined or stopped part-way
                                                is screened normally.
                                            </p>
                                            <textarea
                                                value={message}
                                                onChange={(e) => {
                                                    const next = e.target.value.slice(0, RETURNING_MAX);
                                                    setFlow(prev => (prev ? {
                                                        ...prev,
                                                        returning_visitor: next.trim()
                                                            ? { ...(prev.returning_visitor || {}), enabled: true, message: next }
                                                            : undefined
                                                    } : prev));
                                                }}
                                                rows={3}
                                                maxLength={RETURNING_MAX}
                                                placeholder="e.g. It looks like you've already sent us your details, so there's no need to go through the questions again. For an update, call the office on 000-000-0000 or email us."
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                                            />
                                            <div className={`mt-1 text-xs text-right ${message.length > RETURNING_MAX * 0.9 ? 'text-amber-600' : 'text-gray-400'}`}>
                                                {message.length}/{RETURNING_MAX}
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>

                            {/* Assistant Voice & Introduction */}
                            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                                <div className="flex items-center gap-2 mb-4">
                                    <span className="text-lg">🤖</span>
                                    <h3 className="text-base font-semibold text-gray-900">Assistant Voice & Introduction</h3>
                                </div>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Display Name
                                            <Tooltip text="The name shown to users at the top of the flow">
                                                <FontAwesomeIcon icon={faInfoCircle} className="text-gray-400 text-xs ml-1" />
                                            </Tooltip>
                                        </label>
                                        <input
                                            type="text"
                                            value={flow.title}
                                            onChange={(e) => handleFlowFieldChange('title', e.target.value)}
                                            className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                                            placeholder="Troubleshooting Assistant"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Welcome Message
                                            <Tooltip text="The first message users see when they start the support flow">
                                                <FontAwesomeIcon icon={faInfoCircle} className="text-gray-400 text-xs ml-1" />
                                            </Tooltip>
                                        </label>
                                        <textarea
                                            value={flow.description}
                                            onChange={(e) => handleFlowFieldChange('description', e.target.value)}
                                            rows={3}
                                            className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                                            placeholder="I'll help you troubleshoot your issue step by step. Let's start by understanding what's happening."
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Escalation & AI Guidance */}
                            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                                <div className="flex items-center gap-2 mb-4">
                                    <span className="text-lg">📧</span>
                                    <h3 className="text-base font-semibold text-gray-900">Escalation & AI Guidance</h3>
                                </div>
                                <div className="space-y-5">
                                    {/* Email Notification Toggle */}
                                    <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                                        <div className="flex items-center justify-between mb-3">
                                            <div>
                                                <p className="font-medium text-gray-900">Send email summary when flow completes</p>
                                                <p className="text-sm text-gray-500 mt-1">Get notified when users complete the support flow</p>
                                            </div>
                                            <label className="relative inline-flex cursor-pointer items-center">
                                                <input
                                                    type="checkbox"
                                                    className="peer sr-only"
                                                    checked={flow.post_actions?.email?.enabled !== false}
                                                    onChange={(e) => handleEmailToggle(e.target.checked)}
                                                />
                                                <span className="peer h-5 w-10 rounded-full bg-gray-300 after:absolute after:start-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all peer-checked:bg-brand-500 peer-checked:after:translate-x-full"></span>
                                            </label>
                                        </div>
                                        {flow.post_actions?.email?.enabled !== false && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Recipients (comma-separated)
                                                </label>
                                                <input
                                                    type="text"
                                                    value={flow.post_actions?.email?.to?.join(', ') || ''}
                                                    onChange={(e) => handleEmailRecipientsChange(e.target.value)}
                                                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                                                    placeholder="support@example.com, tickets@example.com"
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {/* Smith.ai Webhook Toggle */}
                                    <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                                        <div className="flex items-center justify-between mb-3">
                                            <div>
                                                <p className="font-medium text-gray-900">Send to Smith.ai webhook when flow completes</p>
                                                <p className="text-sm text-gray-500 mt-1">POST the same summary &amp; transcript to Smith.ai. Works alongside email — enable both to send to both.</p>
                                            </div>
                                            <label className="relative inline-flex cursor-pointer items-center">
                                                <input
                                                    type="checkbox"
                                                    className="peer sr-only"
                                                    checked={flow.post_actions?.webhook?.enabled === true}
                                                    onChange={(e) => handleWebhookToggle(e.target.checked)}
                                                />
                                                <span className="peer h-5 w-10 rounded-full bg-gray-300 after:absolute after:start-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all peer-checked:bg-brand-500 peer-checked:after:translate-x-full"></span>
                                            </label>
                                        </div>
                                        {flow.post_actions?.webhook?.enabled === true && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Webhook URL
                                                </label>
                                                <input
                                                    type="url"
                                                    value={flow.post_actions?.webhook?.url || ''}
                                                    onChange={(e) => handleWebhookUrlChange(e.target.value)}
                                                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                                                    placeholder="https://hooks.smith.ai/..."
                                                />
                                                {(flow.post_actions?.webhook?.url || '').trim() !== '' &&
                                                 !/^https:\/\//i.test((flow.post_actions?.webhook?.url || '').trim()) && (
                                                    <p className="mt-1 text-xs text-red-600">
                                                        URL must start with https:// — lead data is only sent over a secure (HTTPS) connection.
                                                    </p>
                                                )}
                                                <p className="mt-1 text-xs text-gray-500">JSON payload includes collected answers, contact, outcome, and the full transcript. HTTPS only.</p>

                                                <label className="block text-sm font-medium text-gray-700 mb-2 mt-4">
                                                    Secret token (optional)
                                                </label>
                                                <input
                                                    type="password"
                                                    autoComplete="off"
                                                    value={flow.post_actions?.webhook?.auth_token || ''}
                                                    onChange={(e) => handleWebhookTokenChange(e.target.value)}
                                                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                                                    placeholder="Sent as: Authorization: Bearer <token>"
                                                />
                                                <p className="mt-1 text-xs text-gray-500">Sent as an <code>Authorization: Bearer</code> header so your endpoint (or a Zapier/Make filter) can verify the request. Leave blank to skip.</p>
                                            </div>
                                        )}
                                    </div>

                                    {/* Escalation Email Override */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Escalation Email Override (optional)
                                            <Tooltip text="Use a different email for urgent escalations">
                                                <FontAwesomeIcon icon={faInfoCircle} className="text-gray-400 text-xs ml-1" />
                                            </Tooltip>
                                        </label>
                                        <div className="relative">
                                            <span className="absolute inset-y-0 left-3 flex items-center text-gray-400">
                                                <FontAwesomeIcon icon={faEnvelope} />
                                            </span>
                                            <input
                                                type="text"
                                                value={escalationEmail}
                                                onChange={(e) => setEscalationEmail(e.target.value)}
                                                className="w-full rounded-lg border border-gray-300 bg-white pl-10 pr-4 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                                                placeholder="urgent@example.com"
                                            />
                                        </div>
                                        <p className="mt-1 text-xs text-gray-500">Leave blank to use recipients above</p>
                                    </div>

                                    {/* AI Guidance Prompt */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            AI Guidance Prompt (optional)
                                            <Tooltip text="Instructions for how the AI should generate troubleshooting guidance">
                                                <FontAwesomeIcon icon={faInfoCircle} className="text-gray-400 text-xs ml-1" />
                                            </Tooltip>
                                        </label>
                                        <textarea
                                            value={flow.post_actions?.guidance_prompt || ''}
                                            onChange={(e) => handleGuidancePromptChange(e.target.value)}
                                            rows={4}
                                            className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                                            placeholder="Focus on our product features: X, Y, Z. Always suggest checking documentation first. Be empathetic and professional."
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Steps Tab */}
                    {activeTab === 'steps' && (
                        <div className="space-y-6">
                            {/* Steps Header with Add Button */}
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900">Support Flow Steps</h3>
                                    <p className="text-sm text-gray-500 mt-1">
                                        Drag to reorder • Click to edit • {flow.steps.length} total steps
                                    </p>
                                </div>
                                <div className="relative group">
                                    <button
                                        type="button"
                                        className="inline-flex items-center gap-2 rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-800 whitespace-nowrap"
                                    >
                                        <FontAwesomeIcon icon={faPlus} />
                                        Add New Step
                                        <FontAwesomeIcon icon={faChevronDown} className="text-xs" />
                                    </button>
                                    {/* Dropdown */}
                                    <div className="absolute right-0 mt-2 w-56 rounded-lg border border-gray-200 bg-white shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                                        {STEP_TEMPLATES.map(template => (
                                            <button
                                                key={template.type}
                                                type="button"
                                                onClick={() => handleAddStep(template.type)}
                                                className="w-full text-left px-4 py-3 hover:bg-brand-50 transition flex items-start gap-3 border-b border-gray-100 last:border-0"
                                            >
                                                <span className="text-lg">{template.icon}</span>
                                                <div>
                                                    <p className="font-medium text-sm text-gray-900">{template.label}</p>
                                                    <p className="text-xs text-gray-500">{template.helper}</p>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Steps List with Drag and Drop */}
                            <DragDropContext onDragEnd={handleDragEnd}>
                                <Droppable droppableId="steps">
                                    {(provided: DroppableProvided) => (
                                        <div
                                            {...provided.droppableProps}
                                            ref={provided.innerRef}
                                            className="space-y-3"
                                        >
                                            {flow.steps.map((step, index) => {
                                                const isExpanded = expandedStepId === step.id;
                                                return (
                                                    <React.Fragment key={step.id}>
                                                        <Draggable draggableId={step.id} index={index}>
                                                            {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
                                                                <div
                                                                    ref={provided.innerRef}
                                                                    {...provided.draggableProps}
                                                                    className={`rounded-xl border bg-white shadow-sm transition ${
                                                                        snapshot.isDragging
                                                                            ? 'border-brand-500 shadow-lg'
                                                                            : isExpanded
                                                                            ? 'border-brand-300'
                                                                            : 'border-gray-200'
                                                                    }`}
                                                                >
                                                                    {/* Step Header */}
                                                                    <div className="flex items-center gap-3 p-4">
                                                                        {/* Drag Handle */}
                                                                        <div
                                                                            {...provided.dragHandleProps}
                                                                            className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
                                                                        >
                                                                            <FontAwesomeIcon icon={faGripVertical} />
                                                                        </div>

                                                                        {/* Step Info */}
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setExpandedStepId(isExpanded ? null : step.id)}
                                                                            className="flex-1 text-left"
                                                                        >
                                                                            <div className="flex items-center gap-2">
                                                                                <FontAwesomeIcon
                                                                                    icon={isExpanded ? faChevronDown : faChevronRight}
                                                                                    className="text-gray-400 text-sm"
                                                                                />
                                                                                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                                                                    Step {index + 1}
                                                                                </span>
                                                                                <span className="text-xs text-gray-400">•</span>
                                                                                <span className="text-xs text-gray-600">
                                                                                    {STEP_TEMPLATES.find(t => t.type === step.type)?.label || step.type}
                                                                                </span>
                                                                                {step.required && (
                                                                                    <>
                                                                                        <span className="text-xs text-gray-400">•</span>
                                                                                        <span className="text-xs font-medium text-brand-500">Required</span>
                                                                                    </>
                                                                                )}
                                                                            </div>
                                                                            <p className="font-semibold text-gray-900 mt-1">
                                                                                {step.title || 'Untitled step'}
                                                                            </p>
                                                                            {step.description && (
                                                                                <p className="text-sm text-gray-500 mt-1 line-clamp-1">
                                                                                    {step.description}
                                                                                </p>
                                                                            )}
                                                                        </button>

                                                                        {/* Actions */}
                                                                        <div className="flex items-center gap-2">
                                                                            <Tooltip text="Duplicate this step">
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => handleDuplicateStep(step.id)}
                                                                                    className="p-2 text-gray-400 hover:text-brand-500 hover:bg-brand-50 rounded transition"
                                                                                >
                                                                                    <FontAwesomeIcon icon={faCopy} />
                                                                                </button>
                                                                            </Tooltip>
                                                                            <Tooltip text="Delete this step">
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => handleRemoveStep(step.id)}
                                                                                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition"
                                                                                >
                                                                                    <FontAwesomeIcon icon={faTrash} />
                                                                                </button>
                                                                            </Tooltip>
                                                                        </div>
                                                                    </div>

                                                                    {/* Expanded Step Details */}
                                                                    {isExpanded && (
                                                                        <div className="border-t border-gray-200 p-6 bg-gray-50">
                                                                            <h4 className="text-sm font-semibold text-gray-900 mb-4">Step Configuration</h4>

                                                                            {/* Basic Fields */}
                                                                            <div className="grid gap-4 md:grid-cols-2 mb-4">
                                                                                <div>
                                                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                                        Display Title
                                                                                    </label>
                                                                                    <input
                                                                                        type="text"
                                                                                        value={step.title || ''}
                                                                                        onChange={(e) => handleStepUpdate(step.id, { title: e.target.value })}
                                                                                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                                                                                        placeholder="What's your question?"
                                                                                    />
                                                                                </div>
                                                                                <div>
                                                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                                        Step Type
                                                                                    </label>
                                                                                    <select
                                                                                        value={step.type}
                                                                                        onChange={(e) => handleStepTypeChange(step.id, e.target.value as StepType)}
                                                                                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                                                                                    >
                                                                                        {STEP_TEMPLATES.map(template => (
                                                                                            <option key={template.type} value={template.type}>
                                                                                                {template.icon} {template.label}
                                                                                            </option>
                                                                                        ))}
                                                                                    </select>
                                                                                </div>
                                                                            </div>

                                                                            <div className="mb-4">
                                                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                                    Helper Text (optional)
                                                                                </label>
                                                                                <textarea
                                                                                    value={step.description || ''}
                                                                                    onChange={(e) => handleStepUpdate(step.id, { description: e.target.value })}
                                                                                    rows={2}
                                                                                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                                                                                    placeholder="Additional context or instructions for this step"
                                                                                />
                                                                            </div>

                                                                            {/* Required Toggle */}
                                                                            <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3 mb-4">
                                                                                <div>
                                                                                    <p className="font-medium text-gray-900">Required</p>
                                                                                    <p className="text-sm text-gray-500">User must answer before continuing</p>
                                                                                </div>
                                                                                <label className="relative inline-flex cursor-pointer items-center">
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        className="peer sr-only"
                                                                                        checked={step.required !== false}
                                                                                        onChange={(e) => handleStepUpdate(step.id, { required: e.target.checked })}
                                                                                    />
                                                                                    <span className="peer h-5 w-10 rounded-full bg-gray-300 after:absolute after:start-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all peer-checked:bg-brand-500 peer-checked:after:translate-x-full"></span>
                                                                                </label>
                                                                            </div>

                                                                            {/* Type-specific fields */}
                                                                            {['single_select', 'multi_select'].includes(step.type) && (
                                                                                <div className="rounded-lg border border-brand-200 bg-brand-50 p-4">
                                                                                    <div className="flex items-center justify-between mb-3">
                                                                                        <p className="text-sm font-semibold text-brand-800">Answer Options</p>
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={() => handleAddOption(step.id)}
                                                                                            className="inline-flex items-center gap-1 rounded-lg border border-brand-300 bg-white px-3 py-1 text-xs font-semibold text-brand-600 hover:bg-brand-50 whitespace-nowrap"
                                                                                        >
                                                                                            <FontAwesomeIcon icon={faPlus} />
                                                                                            Add Option
                                                                                        </button>
                                                                                    </div>
                                                                                    <div className="space-y-2">
                                                                                        {(step.options || []).map((option, idx) => (
                                                                                            <div key={option.value} className="rounded-lg border border-brand-100 bg-white p-3">
                                                                                                <div className="grid gap-2 md:grid-cols-2">
                                                                                                    <div>
                                                                                                        <label className="block text-xs font-semibold text-brand-700 mb-1">Label</label>
                                                                                                        <input
                                                                                                            type="text"
                                                                                                            value={option.label}
                                                                                                            onChange={(e) => handleOptionUpdate(step.id, idx, 'label', e.target.value)}
                                                                                                            className="w-full rounded-md border border-brand-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
                                                                                                        />
                                                                                                    </div>
                                                                                                    <div>
                                                                                                        <label className="block text-xs font-semibold text-brand-700 mb-1">Value</label>
                                                                                                        <input
                                                                                                            type="text"
                                                                                                            value={option.value}
                                                                                                            onChange={(e) => handleOptionUpdate(step.id, idx, 'value', e.target.value)}
                                                                                                            className="w-full rounded-md border border-brand-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
                                                                                                        />
                                                                                                    </div>
                                                                                                </div>
                                                                                                <button
                                                                                                    type="button"
                                                                                                    onClick={() => handleRemoveOption(step.id, idx)}
                                                                                                    className="mt-2 text-xs text-red-600 hover:text-red-700"
                                                                                                >
                                                                                                    Remove option
                                                                                                </button>
                                                                                            </div>
                                                                                        ))}
                                                                                    </div>
                                                                                    {step.type === 'multi_select' && (
                                                                                        <label className="inline-flex items-center gap-2 text-sm text-brand-800 mt-3">
                                                                                            <input
                                                                                                type="checkbox"
                                                                                                className="h-4 w-4 rounded border-brand-300 text-brand-500"
                                                                                                checked={step.allow_other === true}
                                                                                                onChange={(e) => handleStepUpdate(step.id, { allow_other: e.target.checked })}
                                                                                            />
                                                                                            Allow "Other" response
                                                                                        </label>
                                                                                    )}
                                                                                </div>
                                                                            )}

                                                                            {step.type === 'long_text' && (
                                                                                <div className="grid gap-3 md:grid-cols-2">
                                                                                    <div>
                                                                                        <label className="block text-sm font-medium text-gray-700 mb-2">Placeholder</label>
                                                                                        <input
                                                                                            type="text"
                                                                                            value={step.placeholder || ''}
                                                                                            onChange={(e) => handleStepUpdate(step.id, { placeholder: e.target.value })}
                                                                                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                                                                                        />
                                                                                    </div>
                                                                                    <div>
                                                                                        <label className="block text-sm font-medium text-gray-700 mb-2">Min characters</label>
                                                                                        <input
                                                                                            type="number"
                                                                                            min={0}
                                                                                            value={step.min_length ?? 0}
                                                                                            onChange={(e) => handleStepUpdate(step.id, { min_length: Number(e.target.value) })}
                                                                                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                                                                                        />
                                                                                    </div>
                                                                                </div>
                                                                            )}

                                                                            {step.type === 'text' && (
                                                                                <div>
                                                                                    <label className="block text-sm font-medium text-gray-700 mb-2">Placeholder</label>
                                                                                    <input
                                                                                        type="text"
                                                                                        value={step.placeholder || ''}
                                                                                        onChange={(e) => handleStepUpdate(step.id, { placeholder: e.target.value })}
                                                                                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                                                                                    />
                                                                                </div>
                                                                            )}

                                                                            {step.type === 'contact' && (
                                                                                <div className="space-y-3">
                                                                                    <div className="flex items-center justify-between">
                                                                                        <p className="text-sm font-semibold text-gray-800">Contact Fields</p>
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={() => handleAddContactField(step.id)}
                                                                                            className="inline-flex items-center gap-1 rounded-lg border border-brand-300 bg-white px-3 py-1 text-xs font-semibold text-brand-600 whitespace-nowrap"
                                                                                        >
                                                                                            <FontAwesomeIcon icon={faPlus} />
                                                                                            Add Field
                                                                                        </button>
                                                                                    </div>
                                                                                    {(step.fields || []).map((field, idx) => (
                                                                                        <div key={field.id} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                                                                                            <div className="grid gap-3 md:grid-cols-2 mb-2">
                                                                                                <div>
                                                                                                    <label className="block text-xs font-semibold text-gray-700 mb-1">Label</label>
                                                                                                    <input
                                                                                                        type="text"
                                                                                                        value={field.label}
                                                                                                        onChange={(e) => handleContactFieldUpdate(step.id, idx, 'label', e.target.value)}
                                                                                                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                                                                                                    />
                                                                                                </div>
                                                                                                <div>
                                                                                                    <label className="block text-xs font-semibold text-gray-700 mb-1">Type</label>
                                                                                                    <select
                                                                                                        value={field.type}
                                                                                                        onChange={(e) => handleContactFieldUpdate(step.id, idx, 'type', e.target.value)}
                                                                                                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                                                                                                    >
                                                                                                        <option value="text">Text</option>
                                                                                                        <option value="email">Email</option>
                                                                                                        <option value="phone">Phone</option>
                                                                                                        <option value="number">Number</option>
                                                                                                    </select>
                                                                                                </div>
                                                                                            </div>
                                                                                            <div className="flex items-center justify-between">
                                                                                                <label className="inline-flex items-center gap-2 text-xs">
                                                                                                    <input
                                                                                                        type="checkbox"
                                                                                                        className="h-4 w-4 rounded"
                                                                                                        checked={field.required !== false}
                                                                                                        onChange={(e) => handleContactFieldUpdate(step.id, idx, 'required', e.target.checked)}
                                                                                                    />
                                                                                                    Required
                                                                                                </label>
                                                                                                <button
                                                                                                    type="button"
                                                                                                    onClick={() => handleRemoveContactField(step.id, idx)}
                                                                                                    className="text-xs text-red-600 hover:text-red-700"
                                                                                                >
                                                                                                    Remove
                                                                                                </button>
                                                                                            </div>
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            )}

                                                                            {step.type === 'image' && (
                                                                                <div className="space-y-3">
                                                                                    <div className="grid gap-3 md:grid-cols-2">
                                                                                        <div>
                                                                                            <label className="block text-sm font-medium text-gray-700 mb-2">Accept File Types</label>
                                                                                            <input
                                                                                                type="text"
                                                                                                value={step.accept || 'image/*'}
                                                                                                onChange={(e) => handleStepUpdate(step.id, { accept: e.target.value })}
                                                                                                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                                                                                                placeholder="e.g., image/*, .jpg, .png"
                                                                                            />
                                                                                        </div>
                                                                                        <div>
                                                                                            <label className="block text-sm font-medium text-gray-700 mb-2">Max Size (MB)</label>
                                                                                            <input
                                                                                                type="number"
                                                                                                value={step.max_size_mb || 10}
                                                                                                onChange={(e) => handleStepUpdate(step.id, { max_size_mb: parseInt(e.target.value) || 10 })}
                                                                                                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                                                                                                min="1"
                                                                                                max="50"
                                                                                            />
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className="flex items-center gap-4">
                                                                                        <label className="inline-flex items-center gap-2 text-sm">
                                                                                            <input
                                                                                                type="checkbox"
                                                                                                className="h-4 w-4 rounded"
                                                                                                checked={step.multiple || false}
                                                                                                onChange={(e) => handleStepUpdate(step.id, { multiple: e.target.checked })}
                                                                                            />
                                                                                            Allow multiple files
                                                                                        </label>
                                                                                    </div>
                                                                                    <div className="bg-brand-50 border border-brand-200 rounded-lg p-3">
                                                                                        <p className="text-xs text-brand-600">
                                                                                            <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
                                                                                            Users will be able to upload images to help describe their issue
                                                                                        </p>
                                                                                    </div>
                                                                                </div>
                                                                            )}

                                                                            {/* When to ask this question (branching) */}
                                                                            {(() => {
                                                                                const priorSteps = flow.steps.slice(0, index);
                                                                                const vis = step.visible_if;
                                                                                return (
                                                                                    <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
                                                                                        <div className="flex items-start justify-between gap-3">
                                                                                            <div>
                                                                                                <p className="text-sm font-medium text-gray-700">
                                                                                                    When to ask this question
                                                                                                </p>
                                                                                                <p className="text-xs text-gray-500 mt-0.5">
                                                                                                    {index === 0
                                                                                                        ? 'The first question is always asked.'
                                                                                                        : vis
                                                                                                            ? 'Only asked when the answers below match. Skipped otherwise.'
                                                                                                            : 'Always asked. Add a condition to ask it only for certain people.'}
                                                                                                </p>
                                                                                            </div>
                                                                                            {index > 0 && (
                                                                                                <label className="inline-flex shrink-0 items-center gap-2 text-xs font-medium text-gray-700">
                                                                                                    <input
                                                                                                        type="checkbox"
                                                                                                        className="h-4 w-4 rounded"
                                                                                                        checked={!!vis}
                                                                                                        onChange={(e) => handleVisibilityToggle(step.id, e.target.checked)}
                                                                                                    />
                                                                                                    Only ask sometimes
                                                                                                </label>
                                                                                            )}
                                                                                        </div>

                                                                                        {vis && (
                                                                                            <div className="mt-3 space-y-2">
                                                                                                <div className="flex items-center gap-2 text-sm">
                                                                                                    <span className="text-gray-700">Ask this only if</span>
                                                                                                    <select
                                                                                                        value={vis.match}
                                                                                                        onChange={(e) => handleVisibilityMatch(step.id, e.target.value as 'all' | 'any')}
                                                                                                        className="border border-gray-300 rounded-md px-2 py-1 bg-white text-sm"
                                                                                                    >
                                                                                                        <option value="all">all</option>
                                                                                                        <option value="any">any</option>
                                                                                                    </select>
                                                                                                    <span className="text-gray-700">of these are true:</span>
                                                                                                </div>

                                                                                                {vis.conditions.map((cond, ci) => {
                                                                                                    const target = flow.steps.find(s => s.id === cond.step);
                                                                                                    const opts = target?.options || [];
                                                                                                    return (
                                                                                                        <div key={ci} className="flex flex-wrap items-center gap-2">
                                                                                                            <select
                                                                                                                value={cond.step}
                                                                                                                onChange={(e) => handleVisibilityCondition(step.id, ci, { step: e.target.value })}
                                                                                                                title={flow.steps.find(s => s.id === cond.step)?.title || cond.step}
                                                                                                                className="border border-gray-300 rounded-md px-2 py-1 bg-white text-sm min-w-0 w-full sm:w-auto sm:max-w-[15rem] truncate"
                                                                                                            >
                                                                                                                {priorSteps.map(s => (
                                                                                                                    <option key={s.id} value={s.id}>{s.title || s.id}</option>
                                                                                                                ))}
                                                                                                            </select>
                                                                                                            <select
                                                                                                                value={cond.op}
                                                                                                                onChange={(e) => handleVisibilityCondition(step.id, ci, { op: e.target.value })}
                                                                                                                className="border border-gray-300 rounded-md px-2 py-1 bg-white text-sm"
                                                                                                            >
                                                                                                                <option value="equals">is</option>
                                                                                                                <option value="not_equals">is not</option>
                                                                                                                <option value="contains">contains</option>
                                                                                                                <option value="not_contains">does not contain</option>
                                                                                                            </select>
                                                                                                            {opts.length > 0 ? (
                                                                                                                <select
                                                                                                                    value={cond.value}
                                                                                                                    onChange={(e) => handleVisibilityCondition(step.id, ci, { value: e.target.value })}
                                                                                                                    className="border border-gray-300 rounded-md px-2 py-1 bg-white text-sm"
                                                                                                                >
                                                                                                                    {opts.map(o => (
                                                                                                                        <option key={o.value} value={o.value}>{o.label || o.value}</option>
                                                                                                                    ))}
                                                                                                                </select>
                                                                                                            ) : (
                                                                                                                <input
                                                                                                                    type="text"
                                                                                                                    value={cond.value}
                                                                                                                    placeholder="answer"
                                                                                                                    onChange={(e) => handleVisibilityCondition(step.id, ci, { value: e.target.value })}
                                                                                                                    className="border border-gray-300 rounded-md px-2 py-1 bg-white text-sm flex-1 min-w-[8rem]"
                                                                                                                />
                                                                                                            )}
                                                                                                            {vis.conditions.length > 1 && (
                                                                                                                <button
                                                                                                                    type="button"
                                                                                                                    onClick={() => handleVisibilityConditionRemove(step.id, ci)}
                                                                                                                    className="text-gray-400 hover:text-red-600 px-1"
                                                                                                                    aria-label="Remove condition"
                                                                                                                >✕</button>
                                                                                                            )}
                                                                                                        </div>
                                                                                                    );
                                                                                                })}

                                                                                                <button
                                                                                                    type="button"
                                                                                                    onClick={() => handleVisibilityConditionAdd(step.id)}
                                                                                                    className="text-brand-500 hover:text-brand-700 text-sm font-medium"
                                                                                                >+ Add condition</button>
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                );
                                                                            })()}

                                                                            {/* Advanced Settings */}
                                                                            <details className="mt-4">
                                                                                <summary className="text-sm font-medium text-gray-700 cursor-pointer hover:text-gray-900">
                                                                                    Advanced Settings
                                                                                </summary>
                                                                                <div className="mt-3">
                                                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                                        Internal Step ID
                                                                                    </label>
                                                                                    <input
                                                                                        type="text"
                                                                                        value={step.id}
                                                                                        onChange={(e) => handleStepIdChange(step.id, e.target.value)}
                                                                                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-sans"
                                                                                    />
                                                                                    <p className="mt-1 text-xs text-gray-500">
                                                                                        Lowercase, alphanumeric, underscores and hyphens only
                                                                                    </p>
                                                                                </div>
                                                                            </details>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </Draggable>

                                                        {/* Inline Add Step Button */}
                                                        <div className="relative group">
                                                            <button
                                                                type="button"
                                                                className="w-full py-2 text-sm text-gray-400 hover:text-brand-500 border-2 border-dashed border-gray-200 hover:border-brand-300 rounded-lg transition opacity-0 group-hover:opacity-100"
                                                            >
                                                                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                                                                Add step here
                                                            </button>
                                                            <div className="absolute left-1/2 transform -translate-x-1/2 mt-2 w-56 rounded-lg border border-gray-200 bg-white shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                                                                {STEP_TEMPLATES.map(template => (
                                                                    <button
                                                                        key={template.type}
                                                                        type="button"
                                                                        onClick={() => handleAddStep(template.type, index)}
                                                                        className="w-full text-left px-4 py-3 hover:bg-brand-50 transition flex items-start gap-3 border-b border-gray-100 last:border-0"
                                                                    >
                                                                        <span className="text-lg">{template.icon}</span>
                                                                        <div>
                                                                            <p className="font-medium text-sm text-gray-900">{template.label}</p>
                                                                            <p className="text-xs text-gray-500">{template.helper}</p>
                                                                        </div>
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </React.Fragment>
                                                );
                                            })}
                                            {provided.placeholder}
                                        </div>
                                    )}
                                </Droppable>
                            </DragDropContext>

                            {/* Quick Add Step Types */}
                            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                                <p className="text-sm font-semibold text-gray-900 mb-4">Quick Add Step Type</p>
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                                    {STEP_TEMPLATES.map(template => (
                                        <button
                                            key={template.type}
                                            type="button"
                                            onClick={() => handleAddStep(template.type)}
                                            className="flex flex-col items-center gap-2 rounded-lg border border-gray-200 bg-white p-4 text-center hover:border-brand-300 hover:bg-brand-50 transition"
                                        >
                                            <span className="text-2xl">{template.icon}</span>
                                            <p className="font-medium text-sm text-gray-900">{template.label}</p>
                                            <p className="text-xs text-gray-500">{template.helper}</p>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Exit Rules Tab */}
                    {activeTab === 'exits' && (() => {
                        const OPERATORS: { value: string; label: string }[] = [
                            { value: 'equals', label: 'is' },
                            { value: 'not_equals', label: 'is not' },
                            { value: 'in', label: 'is any of (comma-sep)' },
                            { value: 'not_in', label: 'is none of (comma-sep)' },
                            { value: 'contains', label: 'contains' },
                            { value: 'not_contains', label: 'does not contain' },
                            { value: 'gt', label: '> (greater than)' },
                            { value: 'gte', label: '≥ (at least)' },
                            { value: 'lt', label: '< (less than)' },
                            { value: 'lte', label: '≤ (at most)' },
                        ];
                        const rules = flow.exit_rules || [];
                        const setRules = (next: any[]) =>
                            setFlow(prev => (prev ? { ...prev, exit_rules: next } : prev));
                        const updateRule = (i: number, patch: any) =>
                            setRules(rules.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
                        const updateCond = (ri: number, ci: number, patch: any) =>
                            updateRule(ri, {
                                conditions: rules[ri].conditions.map((c, idx) =>
                                    idx === ci ? { ...c, ...patch } : c)
                            });
                        return (
                            <div className="space-y-4">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900">Early-Exit Rules</h3>
                                        <p className="text-sm text-gray-500 mt-1 max-w-2xl">
                                            React to a user's answers with a fixed message. Rules are checked top-to-bottom
                                            after every answer and the first match wins. Choose <strong>End the flow</strong> to
                                            stop there (used for disqualification — the team is not emailed, so give the person
                                            somewhere else to go), or <strong>Show a message and keep going</strong> to give
                                            guidance and carry on screening so they can still become a lead.
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => setRules([...rules, {
                                            id: `exit_${Date.now().toString(36)}`,
                                            match: 'any',
                                            conditions: [{ step: flow.steps[0]?.id || '', op: 'equals', value: '' }],
                                            message: ''
                                        }])}
                                        disabled={flow.steps.length === 0}
                                        className="px-3 py-2 text-sm font-medium rounded-lg bg-black text-white hover:bg-gray-800 disabled:opacity-50"
                                    >
                                        + Add Rule
                                    </button>
                                </div>

                                {flow.steps.length === 0 && (
                                    <p className="text-sm text-amber-600">Add at least one step first — exit rules check answers to your steps.</p>
                                )}

                                {rules.length === 0 && flow.steps.length > 0 && (
                                    <p className="text-sm text-gray-400 italic">No exit rules yet. The flow will simply collect all steps and complete.</p>
                                )}

                                {rules.map((rule, ri) => (
                                    <div key={rule.id || ri} className="border border-gray-200 rounded-xl p-4 bg-gray-50 space-y-3">
                                        <div className="flex flex-wrap items-center gap-2 text-sm">
                                            <select
                                                value={rule.mode === 'notice' ? 'notice' : 'end'}
                                                onChange={(e) => updateRule(ri, { mode: e.target.value })}
                                                className="border border-gray-300 rounded-md px-2 py-1 bg-white font-medium text-gray-700"
                                            >
                                                <option value="end">End the flow</option>
                                                <option value="notice">Show a message and keep going</option>
                                            </select>
                                            <span className="font-medium text-gray-700">when</span>
                                            <select
                                                value={rule.match}
                                                onChange={(e) => updateRule(ri, { match: e.target.value })}
                                                className="border border-gray-300 rounded-md px-2 py-1 bg-white"
                                            >
                                                <option value="any">any</option>
                                                <option value="all">all</option>
                                            </select>
                                            <span className="font-medium text-gray-700">of these are true:</span>
                                            <button onClick={() => setRules(rules.filter((_, idx) => idx !== ri))}
                                                className="ml-auto text-red-500 hover:text-red-700 text-sm">Remove rule</button>
                                        </div>
                                        {rule.mode === 'notice' && (
                                            <p className="text-xs text-gray-600 bg-brand-50 border border-brand-200 rounded-md px-3 py-2">
                                                The visitor sees this message once, then carries on answering the
                                                remaining questions — so they can still become a lead.
                                            </p>
                                        )}

                                        {/* A select sizes itself to its longest option, and the options here
                                            are whole question titles — so without a width cap the row grew
                                            past the card. Cap it and let the label ellipsis instead. */}
                                        {rule.conditions.map((cond, ci) => (
                                            <div key={ci} className="flex flex-wrap items-center gap-2 min-w-0">
                                                <select
                                                    value={cond.step}
                                                    onChange={(e) => updateCond(ri, ci, { step: e.target.value })}
                                                    title={flow.steps.find(s => s.id === cond.step)?.title || cond.step}
                                                    className="border border-gray-300 rounded-md px-2 py-1 bg-white text-sm min-w-0 w-full sm:w-auto sm:max-w-[18rem] truncate"
                                                >
                                                    {flow.steps.map(s => (
                                                        <option key={s.id} value={s.id}>{s.title || s.id}</option>
                                                    ))}
                                                </select>
                                                <select
                                                    value={cond.op}
                                                    onChange={(e) => updateCond(ri, ci, { op: e.target.value })}
                                                    className="border border-gray-300 rounded-md px-2 py-1 bg-white text-sm shrink-0"
                                                >
                                                    {OPERATORS.map(o => (<option key={o.value} value={o.value}>{o.label}</option>))}
                                                </select>
                                                <input
                                                    type="text"
                                                    value={cond.value}
                                                    placeholder="value (e.g. Yes, 30, $1600)"
                                                    onChange={(e) => updateCond(ri, ci, { value: e.target.value })}
                                                    className="border border-gray-300 rounded-md px-2 py-1 bg-white text-sm flex-1 basis-[140px] min-w-0 max-w-full"
                                                />
                                                {rule.conditions.length > 1 && (
                                                    <button onClick={() => updateRule(ri, { conditions: rule.conditions.filter((_, idx) => idx !== ci) })}
                                                        className="text-gray-400 hover:text-red-600 px-1 shrink-0">✕</button>
                                                )}
                                            </div>
                                        ))}

                                        <button
                                            onClick={() => updateRule(ri, { conditions: [...rule.conditions, { step: flow.steps[0]?.id || '', op: 'equals', value: '' }] })}
                                            className="text-brand-500 hover:text-brand-700 text-sm font-medium"
                                        >+ Add condition</button>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Message to show the visitor</label>
                                            <textarea
                                                value={rule.message}
                                                onChange={(e) => updateRule(ri, { message: e.target.value })}
                                                rows={4}
                                                placeholder="e.g. We're sorry — we can only help people who haven't qualified for benefits yet…"
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        );
                    })()}

                    {/* History Tab */}
                    {activeTab === 'history' && (
                        <div className="space-y-4">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">Version History</h3>
                                <p className="text-sm text-gray-500 mt-1">
                                    View and restore previous versions of this support flow
                                </p>
                            </div>

                            {isDirty && (
                                <div className="rounded-xl border border-amber-300 bg-amber-50 p-5">
                                    <div className="flex items-center gap-3 mb-2">
                                        <div className="h-3 w-3 rounded-full bg-amber-500 animate-pulse"></div>
                                        <p className="font-semibold text-amber-900">Current (Unsaved Changes)</p>
                                    </div>
                                    <p className="text-sm text-amber-800 mb-3">
                                        Modified {Math.floor((Date.now() - (initialSnapshotRef.current ? 0 : 120000)) / 60000)} minutes ago
                                    </p>
                                    <button
                                        onClick={handleReset}
                                        className="text-sm font-medium text-amber-700 hover:text-amber-900 underline"
                                    >
                                        Discard Changes
                                    </button>
                                </div>
                            )}

                            {versions.length === 0 ? (
                                <div className="rounded-xl border border-gray-200 bg-gray-50 p-8 text-center">
                                    <p className="text-sm text-gray-500">No previous versions yet.</p>
                                    <p className="text-xs text-gray-400 mt-1">Versions will appear here after you save changes.</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {versions.map((version, idx) => (
                                        <div key={version.id} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                                            <div className="flex items-start justify-between mb-3">
                                                <div>
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <p className="font-semibold text-gray-900">
                                                            Version {versions.length - idx}
                                                        </p>
                                                        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                                                            version.is_enabled
                                                                ? 'bg-brand-100 text-brand-700'
                                                                : 'bg-gray-200 text-gray-600'
                                                        }`}>
                                                            {version.is_enabled ? '✓ Live' : 'Disabled'}
                                                        </span>
                                                    </div>
                                                    <p className="text-sm text-gray-500">
                                                        {new Date(version.updated_at || version.created_at || '').toLocaleString('en-US', {
                                                            month: 'short',
                                                            day: 'numeric',
                                                            year: 'numeric',
                                                            hour: 'numeric',
                                                            minute: '2-digit'
                                                        })}
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        type="button"
                                                        className="text-sm font-medium text-gray-600 hover:text-gray-900"
                                                    >
                                                        View
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="text-sm font-medium text-brand-500 hover:text-brand-600"
                                                    >
                                                        Restore
                                                    </button>
                                                </div>
                                            </div>
                                            {version.escalation_email && (
                                                <p className="text-xs text-gray-500">
                                                    <FontAwesomeIcon icon={faEnvelope} className="mr-1" />
                                                    Escalates to: {version.escalation_email}
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Sticky Action Bar */}
                <div className="sticky bottom-0 flex items-center justify-end gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-lg">
                    <Tooltip text="See how customers will experience this flow">
                        <button
                            type="button"
                            onClick={() => setShowPreview(true)}
                            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 whitespace-nowrap"
                        >
                            <FontAwesomeIcon icon={faEye} />
                            Preview Flow
                        </button>
                    </Tooltip>
                    <Tooltip text="Reset all changes (Cmd/Ctrl+Z)">
                        <button
                            type="button"
                            onClick={handleReset}
                            disabled={!isDirty}
                            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                        >
                            <FontAwesomeIcon icon={faRotateLeft} />
                            Reset
                        </button>
                    </Tooltip>
                    <Tooltip text="Save changes (Cmd/Ctrl+S)">
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={!canSave}
                            className={`whitespace-nowrap inline-flex items-center gap-2 rounded-lg px-6 py-2 text-sm font-semibold text-white shadow-sm ${
                                canSave
                                    ? 'bg-black hover:bg-gray-800'
                                    : 'bg-brand-300 cursor-not-allowed'
                            }`}
                        >
                            {savingConfig ? (
                                <>
                                    <FontAwesomeIcon icon={faCircleNotch} spin />
                                    Saving…
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faFloppyDisk} />
                                    Save Flow
                                </>
                            )}
                        </button>
                    </Tooltip>
                </div>
            </div>

            {/* Modals */}
            <PreviewModal flow={flow} isOpen={showPreview} onClose={() => setShowPreview(false)} />
            <UnsavedChangesModal
                isOpen={showUnsavedModal}
                onDiscard={handleDiscardAndSwitch}
                onCancel={() => {
                    setShowUnsavedModal(false);
                    setPendingClientId(null);
                }}
            />
        </>
    );
};

export default SupportFlowEditor;
